import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';

import { actionCreators as directoryCreators, selector as directorySelector } from 'pages/Directory/State';
import { actionCreators as appCreators, selector as appSelector } from 'app/AppState';
import { translate } from 'services/i18nService';
import ModalService from 'services/ModalService';
import ThemeService from 'themes/ThemeService';
import trackingService from 'services/UITrackingService';
import Modal from 'components/Modal/Modal';
import UserStore from 'stores/UserStore';
import RegistrationForm from 'pages/User/RegistrationForm/RegistrationForm';
import CampaignRegistrationOverlay from 'components/CampaignRegistrationOverlay/CampaignRegistrationOverlay';
import VerifyEmailSent from 'components/VerifyEmailSent/VerifyEmailSent';
import ProfileStore from 'stores/ProfileStore';
import getMessagingService from 'services/MessagingService/MessagingService';
import { MODAL_CLOSED } from 'services/MessagingService/EventNames';

import { getRegistrationTitle } from './utils';
import styles from './RegistrationModal.css';


@withRouter
@connect((state, ownProps) => {
  const { genderPreference } = directorySelector(state, ownProps);
  const { registrationModalCaller } = appSelector(state, ownProps);

  return { genderPreference, registrationModalCaller };
}, (dispatch) => {
  const directoryActions = bindActionCreators(directoryCreators, dispatch);
  const appActions = bindActionCreators(appCreators, dispatch);

  const { setIsAnyModalShown } = directoryActions;
  const { setShowRegistrationModal } = appActions;

  return { setIsAnyModalShown, setShowRegistrationModal };
})
@observer
class RegistrationModal extends PureComponent {
  static propTypes = {
    genderPreference: PropTypes.string.isRequired,
    history: PropTypes.object.isRequired,
    setIsAnyModalShown: PropTypes.func.isRequired,
    setShowRegistrationModal: PropTypes.func.isRequired,
    embedProfileV2: PropTypes.bool,
    redirectAfterRegisterPath: PropTypes.string,
    registrationModalCaller: PropTypes.string,
    showRegistrationModal: PropTypes.bool,
    onClose: PropTypes.func,
  };

  static defaultProps = {
    embedProfileV2: false,
    redirectAfterRegisterPath: undefined,
    registrationModalCaller: undefined,
    showRegistrationModal: false,
    onClose: () => undefined,
  };

  constructor(...args) {
    super(...args);

    this.messagingService = getMessagingService();

    this.state = {
      elementClickedId: '',
      secondStepVisible: false,
    };
  }

  componentDidUpdate(prevProps) {
    const { showRegistrationModal, history } = this.props;

    if (showRegistrationModal && !prevProps.showRegistrationModal) {
      trackingService.emit(
        'openRegistrationOverlay',
        { location: history?.location?.pathname }
      );
    }
  }

  get hasProfileImage() {
    const { performerDetails: { profileImageUrl } } = ProfileStore;

    return profileImageUrl && !profileImageUrl.includes('/defaults/');
  }

  checkRedirects = () => {
    const { redirectAfterRegisterPath } = this.props;
    if (redirectAfterRegisterPath) {
      location.href = redirectAfterRegisterPath;
    }
  };

  setStepVisibility = () => {
    const { setIsAnyModalShown } = this.props;
    setIsAnyModalShown?.(true);
    this.setState({ secondStepVisible: true });
  };

  onOpenLoginModal = () => {
    const { setShowRegistrationModal, setIsAnyModalShown, onClose } = this.props;
    const handleOpenRegistrationModal = (open = true) => setShowRegistrationModal(open);
    onClose();
    handleOpenRegistrationModal(false);

    ModalService.showModal(
      ModalService.MODAL_KEYS.LOGIN_MODAL_V2,
      {
        handleOpenRegistrationModal,
        setIsAnyModalShown,
      }
    );
  };

  onCloseModal = () => {
    const {
      history,
      setShowRegistrationModal,
      setIsAnyModalShown,
      onClose,
    } = this.props;
    const { handleResendEmailResponse } = UserStore;

    this.setState({ secondStepVisible: false });
    handleResendEmailResponse(false);
    setShowRegistrationModal(false);
    onClose?.();
    setIsAnyModalShown(false);
    this.messagingService.emit(MODAL_CLOSED);

    trackingService.emit(
      'closeRegistrationOverlay',
      { location: history?.location?.pathname }
    );
  };

  onSendIdToForm = (e) => this.setState({ elementClickedId: e.target.id });

  onAfterRegister = () => {
    this.checkRedirects();
  };

  renderLeftColumn = () => {
    const theme = ThemeService?.theme?.registrationOverlay;

    if (theme?.title && !this.hasProfileImage) {
      return (
        <CampaignRegistrationOverlay className={styles.leftColumn} />
      );
    }

    const { performerDetails: { profileImageUrl } } = ProfileStore;
    if (this.hasProfileImage) {
      const style = { backgroundImage: `url(${profileImageUrl})` };

      return (
        <div
          className={styles.leftColumn}
          data-vwo-id="registration-modal-left-column"
          style={style}
        />
      );
    }

    return (
      <div
        className={styles.leftColumn}
        data-vwo-id="registration-modal-left-column"
      >
        <span className={styles.leftColumnTitle}>
          {translate('profile.profile.registration.form.left.side.text')}
        </span>
      </div>
    );
  };

  render() {
    const {
      showRegistrationModal,
      setIsAnyModalShown,
      genderPreference,
      registrationModalCaller,
      embedProfileV2,
    } = this.props;
    const { secondStepVisible } = this.state;
    const { performerDetails: { username } } = ProfileStore;

    return (
      <Modal
        className={styles.registrationModal}
        // eslint-disable-next-line react/jsx-handler-names
        close={this.onCloseModal}
        dataVwoId="registration-modal-window"
        scroll={!secondStepVisible && !embedProfileV2}
        show={showRegistrationModal}
        showClose={false}
        size={secondStepVisible ? 'small' : 'large'}
        type="full"
      >
        <script
          data-goal="03df84a064f0a0a3fc0ebb8b1a833dd5"
          src="https://a.exoclick.com/tag_gen.js"
          type="application/javascript"
        />
        {secondStepVisible ? (
          <VerifyEmailSent
            setIsAnyModalShown={setIsAnyModalShown}
            onClickClose={this.onCloseModal}
          />
        ) : (
          <div
            className={styles.container}
            data-vwo-id="registration-modal-container"
            onClick={this.onSendIdToForm}
          >
            {this.renderLeftColumn()}
            <div
              className={styles.rightColumn}
              data-vwo-id="registration-modal-right-column"
            >
              <div className={styles.content}>
                <h3>
                  {getRegistrationTitle(registrationModalCaller, username)}
                </h3>
                <RegistrationForm
                  changeRegisterStep={this.setStepVisibility}
                  elementClickedId={this.state.elementClickedId}
                  genderPreference={genderPreference}
                  onAfterRegister={this.onAfterRegister}
                />
                <div className={styles.footer}>
                  <span
                    className={styles.openLoginModal}
                    onClick={this.onOpenLoginModal}
                  >
                    {translate('gold.gold.Already.have.account')}
                    {' '}
                    <span className={styles.signUpHere}>
                      {translate('gold.gold.sign.here')}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    );
  }
}

export default RegistrationModal;
